import React from "react";

// Logo do Tô Alugando em SVG — vetor puro, sem arquivo de imagem: fica nítida
// em qualquer tamanho (favicon de 16px até splash de 512px), muda de cor por
// prop e não gera request de rede.
//
// O símbolo é o ciclo do aluguel: duas setas girando (sai e volta) em torno de
// uma caixa. Cores da marca — o mesmo laranja dos botões e do header
// (orange-600 #ea580c), com orange-500 no topo do gradiente pra dar volume.

export const CORES_MARCA = {
  laranja: '#ea580c',        // orange-600 — cor sólida da marca
  laranjaClaro: '#f97316',   // orange-500 — topo do gradiente
  navy: '#12192a',           // fundo escuro do header
};

// Contorno do ícone. NÃO é um retângulo de canto arredondado: é um squircle,
// o canto contínuo de ícone de app, em que a curvatura entra e sai devagar em
// vez de virar de uma vez num arco. Gerado pelos coeficientes canônicos
// (1.528665 / 0.868867 / 0.631494 / 0.101591 / 0.372823 × raio), os mesmos que
// iOS e Figma usam — com arco simples a quina "estufa" e denuncia na hora.
const SQUIRCLE = "M 50.45,0 L 69.55,0 C 91.33,0 99.16,3.35 107.7,12.3 C 116.65,20.84 120,28.67 120,50.45 L 120,69.55 C 120,91.33 116.65,99.16 107.7,107.7 C 99.16,116.65 91.33,120 69.55,120 L 50.45,120 C 28.67,120 20.84,116.65 12.3,107.7 C 3.35,99.16 0,91.33 0,69.55 L 0,50.45 C 0,28.67 3.35,20.84 12.3,12.3 C 20.84,3.35 28.67,0 50.45,0 Z";

// Traçado do símbolo. Recebe a cor porque ele aparece branco sobre laranja
// (ícone do app) e laranja sobre branco/navy (cabeçalhos e documentos).
//
// Proporção: cada arco cobre 126° e os vãos têm 28°, com as setas medindo 25 de
// largura contra 9 de traço. Isso importa — arco longo com seta do mesmo peso
// do traço fecha visualmente e o desenho vira um anel, não um ciclo girando.
const Simbolo = ({ cor = '#fff' }) => (
  // Nada aqui tem canto vivo: o desenho inteiro é arredondado (linecap e
  // linejoin round), incluindo as pontas de seta e os vértices da caixa.
  <g fill={cor} stroke={cor} strokeLinejoin="round" strokeLinecap="round">
    {/* Arco de cima: sobe pela esquerda, passa no topo, desce à direita */}
    <path d="M 24.29 47.00 A 38 38 0 0 1 89.11 35.57" fill="none" strokeWidth="7" />
    {/* A seta ganha o canto redondo pelo próprio stroke: contorno da MESMA cor
        engorda o triângulo e arredonda as três pontas de uma vez. */}
    <path d="M 97.47 45.53 L 82.22 41.36 L 96.00 29.79 Z" strokeWidth="3" />

    {/* Arco de baixo: espelho exato pelo centro */}
    <path d="M 95.71 73.00 A 38 38 0 0 1 30.89 84.43" fill="none" strokeWidth="7" />
    <path d="M 22.53 74.47 L 37.78 78.64 L 24.00 90.21 Z" strokeWidth="3" />

    {/* Caixa isométrica MONTADA DE TRÊS PEÇAS SOLTAS: cada face é afastada 4,2
        na direção dela mesma (o topo sobe, as laterais abrem), deixando 4,3 de
        vão livre entre elas. Encolher a face não funcionou — o contorno que
        arredonda os vértices soma 1,5 por lado e devolvia o vão inteiro, com as
        faces voltando a se sobrepor. Afastar resolve os dois de uma vez.
        A opacidade decrescente dá o volume sem precisar de segunda cor. */}
    <polygon points="60.00,39.25 76.50,48.55 60.00,57.85 43.50,48.55" strokeWidth="2.5" />
    <polygon points="40.21,54.25 56.71,63.55 56.71,78.85 40.21,69.55" strokeWidth="2.5" opacity="0.9" />
    <polygon points="79.79,54.25 63.29,63.55 63.29,78.85 79.79,69.55" strokeWidth="2.5" opacity="0.74" />
  </g>
);

/**
 * Ícone quadrado (estilo app). Use no header, no favicon e no splash.
 * @param {number} size  lado em px
 * @param {boolean} plano  sem gradiente (para favicon e impressão)
 */
export const LogoIcone = ({ size = 40, plano = false, className = '', ...rest }) => {
  const id = React.useId();
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" className={className}
         role="img" aria-label="Tô Alugando" {...rest}>
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={CORES_MARCA.laranjaClaro} />
          <stop offset="1" stopColor={CORES_MARCA.laranja} />
        </linearGradient>
      </defs>
      <path d={SQUIRCLE} fill={plano ? CORES_MARCA.laranja : `url(#g${id})`} />
      <Simbolo cor="#fff" />
    </svg>
  );
};

/**
 * Símbolo sem a caixa laranja — para fundo claro ou navy.
 */
export const LogoSimbolo = ({ size = 40, cor = CORES_MARCA.laranja, className = '', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" className={className}
       role="img" aria-label="Tô Alugando" {...rest}>
    <Simbolo cor={cor} />
  </svg>
);

/**
 * Assinatura completa: símbolo + nome. `variante` decide o contraste.
 *   'cor'    símbolo laranja + texto navy   (fundo claro)
 *   'branco' tudo branco                     (fundo laranja ou navy)
 *   'navy'   tudo navy                       (impressão, documentos)
 */
export const LogoCompleta = ({ size = 34, variante = 'cor', empilhado = false, className = '' }) => {
  const corSimbolo = variante === 'branco' ? '#fff'
    : variante === 'navy' ? CORES_MARCA.navy : CORES_MARCA.laranja;
  const corTexto = variante === 'branco' ? '#fff' : CORES_MARCA.navy;
  const corDestaque = variante === 'cor' ? CORES_MARCA.laranja
    : variante === 'branco' ? CORES_MARCA.laranjaClaro : CORES_MARCA.navy;

  return (
    <span className={`inline-flex items-center ${empilhado ? 'flex-col gap-1.5' : 'gap-2'} ${className}`}>
      <LogoSimbolo size={size} cor={corSimbolo} />
      <span className="font-extrabold tracking-tight leading-none whitespace-nowrap"
            style={{ fontSize: size * 0.62, letterSpacing: '-0.03em', color: corTexto }}>
        Tô<span style={{ color: corDestaque }}>Alugando</span>
      </span>
    </span>
  );
};

export default LogoIcone;
